import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import PlayArrowOutlinedIcon from "@mui/icons-material/PlayArrowOutlined";
import { toast } from "react-toastify";
import { useThemeColors } from "../../../hooks/useThemeColors";
import apiApplicationService from "../../../service/apiApplicationService";
import { humanize } from "../roles/permissionUtils";
import { ExternalResource, PlaygroundResult } from "./types";

export interface TryItParams {
  slug: string;
  origin: string;
  rowId: string;
  params: Record<string, string>;
}

interface TryItPanelProps {
  resources: ExternalResource[];
  /** Initially selected table; null = the index endpoint. */
  initialResource: ExternalResource | null;
  /** Allowed hosts of the application being edited, offered as origin choices. */
  hosts: string[];
  /** Reports the current request shape so the docs snippets can mirror it. */
  onChange?: (state: TryItParams) => void;
}

const STATUS_HINT: Record<number, string> = {
  401: "The token is missing, malformed, revoked, or not recognised.",
  403: "Either the origin is not in the application's allowed hosts, or the application has no View grant on this table.",
  404: "No row with that id.",
  405: "Writes are not enabled on the external API.",
  429: "Rate limited. Wait for the Retry-After period.",
};

const COMMON_PARAMS = ["page", "page_size", "search", "ordering"];

/**
 * "Try it" console: runs a GET against the external API through the
 * management-side playground proxy with a pasted token. The token lives in
 * component state only.
 */
const TryItPanel: React.FC<TryItPanelProps> = ({ resources, initialResource, hosts, onChange }) => {
  const colors = useThemeColors();
  const headingColor = colors.primary[100];
  const [token, setToken] = useState("");
  const [showToken, setShowToken] = useState(false);
  const [slug, setSlug] = useState(initialResource?.slug ?? "index");
  const [origin, setOrigin] = useState(hosts[0] ?? "");
  const [rowId, setRowId] = useState("");
  const [params, setParams] = useState<Record<string, string>>({});
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<PlaygroundResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const resource = useMemo(() => resources.find((r) => r.slug === slug) ?? null, [resources, slug]);
  const paramNames = useMemo(
    () => (resource ? [...COMMON_PARAMS, ...resource.filterset_fields] : []),
    [resource],
  );

  useEffect(() => {
    setSlug(initialResource?.slug ?? "index");
    setRowId("");
    setParams({});
    setResult(null);
  }, [initialResource]);

  useEffect(() => {
    if (!origin && hosts[0]) setOrigin(hosts[0]);
  }, [hosts, origin]);

  useEffect(() => {
    onChange?.({ slug, origin, rowId, params });
  }, [slug, origin, rowId, params, onChange]);

  const run = async () => {
    if (!token.trim()) {
      setError("Paste the application token first.");
      return;
    }
    setError(null);
    setRunning(true);
    try {
      const res = await apiApplicationService.playground({
        token: token.trim(),
        slug,
        origin: origin.trim(),
        id: slug === "index" || !rowId.trim() ? null : rowId.trim(),
        params,
      });
      setResult(res);
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(detail || "The playground request failed.");
    } finally {
      setRunning(false);
    }
  };

  const statusColor = (code: number) => (code < 300 ? "success" : code < 500 ? "warning" : "error");
  const bodyText = result ? JSON.stringify(result.body, null, 2) : "";
  const mono = { fontFamily: "monospace", fontSize: "0.8125rem" } as const;

  return (
    <Paper sx={{ boxShadow: 0, border: "1px solid", borderColor: "divider", borderRadius: 3, p: 2 }}>
      <Typography fontWeight={700} sx={{ color: headingColor, mb: 0.5 }}>
        Try it
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Paste a token and run a real request. The call is replayed server-side with the origin you pick, so
        allowed-host checks behave exactly as they will for the integration.
      </Typography>
      <Grid container spacing={1.5}>
        <Grid size={{ xs: 12 }}>
          <TextField
            label="Token"
            placeholder="mmsapp_…"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            type={showToken ? "text" : "password"}
            size="small"
            fullWidth
            autoComplete="new-password"
            inputProps={{ name: "mms-api-token", "data-lpignore": "true", "data-1p-ignore": "true" }}
            InputProps={{
              sx: mono,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setShowToken((v) => !v)} edge="end">
                    {showToken ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField select label="Table" value={slug} onChange={(e) => setSlug(e.target.value)} size="small" fullWidth>
            <MenuItem value="index">Index (what this token may read)</MenuItem>
            {resources.map((r) => (
              <MenuItem key={r.slug} value={r.slug}>
                {humanize(r.model)} · /{r.slug}/
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Autocomplete
            freeSolo
            options={hosts}
            value={origin}
            onInputChange={(_, v) => setOrigin(v)}
            renderInput={(p) => (
              <TextField {...p} label="Origin" placeholder="partner.example.com" size="small" />
            )}
          />
        </Grid>
        {resource && (
          <>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                label="Row id (optional)"
                value={rowId}
                onChange={(e) => setRowId(e.target.value)}
                size="small"
                fullWidth
              />
            </Grid>
            {paramNames.map((name) => (
              <Grid key={name} size={{ xs: 6, sm: 4 }}>
                <TextField
                  label={name}
                  value={params[name] ?? ""}
                  onChange={(e) => setParams((prev) => ({ ...prev, [name]: e.target.value }))}
                  size="small"
                  fullWidth
                  disabled={!!rowId.trim()}
                  placeholder={
                    name === "ordering"
                      ? resource.default_ordering[0]
                      : name === "search"
                        ? resource.search_fields[0]
                        : undefined
                  }
                />
              </Grid>
            ))}
          </>
        )}
      </Grid>

      {error && (
        <Alert severity="error" sx={{ mt: 1.5 }}>
          {error}
        </Alert>
      )}

      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5 }}>
        <Button
          variant="contained"
          size="small"
          onClick={run}
          disabled={running}
          startIcon={running ? <CircularProgress size={14} color="inherit" /> : <PlayArrowOutlinedIcon />}
        >
          {running ? "Running…" : "Run request"}
        </Button>
        {result && (
          <>
            <Chip label={`${result.status}`} size="small" color={statusColor(result.status)} />
            <Typography variant="caption" color="text.secondary">
              {result.elapsed_ms} ms · GET {result.request.path}
            </Typography>
          </>
        )}
      </Stack>

      {result && (
        <Box sx={{ mt: 1.5 }}>
          {STATUS_HINT[result.status] && (
            <Alert severity={result.status === 429 ? "warning" : "info"} sx={{ mb: 1 }}>
              {STATUS_HINT[result.status]}
            </Alert>
          )}
          <Paper
            sx={{
              boxShadow: 0,
              bgcolor: colors.primary[800],
              color: colors.primary[100],
              p: 2,
              borderRadius: 2,
              position: "relative",
              maxHeight: 320,
              overflow: "auto",
            }}
          >
            <IconButton
              size="small"
              onClick={() => {
                navigator.clipboard.writeText(bodyText);
                toast.success("Response copied", { position: "top-right" });
              }}
              sx={{ position: "absolute", top: 4, right: 4 }}
            >
              <ContentCopyIcon fontSize="inherit" />
            </IconButton>
            <pre style={{ margin: 0, ...mono, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{bodyText}</pre>
          </Paper>
        </Box>
      )}
    </Paper>
  );
};

export default TryItPanel;
